import React from 'react';
import { Lock, Menu, Facebook, Twitter, Linkedin, Instagram, MapPin, Phone, Mail, ShieldCheck } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';
  const isLandingPage = location.pathname === '/';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo area */}
            <div className="flex-shrink-0 flex items-center gap-2 cursor-pointer">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/logo.png" 
                  alt="Speedlink Logo" 
                  className="w-15 h-10"
                />
                <div className="flex flex-col">
                  
                </div> 
              </Link>
            </div>

            {/* Desktop Nav - Only show on Landing Page */}
            {isLandingPage && (
              <nav className="hidden md:flex space-x-8 text-sm font-semibold text-gray-700">
                <a href="#" className="hover:text-primary transition-colors">Explore</a>
                <a href="#" className="hover:text-primary transition-colors">Individual Training</a>
                <a href="#" className="hover:text-primary transition-colors">Corporate Training</a>
                <a href="#" className="hover:text-primary transition-colors">Certification</a>
                <a href="#" className="hover:text-primary transition-colors">Blog</a>
                <a href="#about" className="hover:text-primary transition-colors">About us</a>
              </nav>
            )}

            {/* Right Actions */}
            <div className="flex items-center space-x-4">
              {isAuthPage ? (
                <Link to="/" className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-100 rounded-lg text-sm font-bold text-primary hover:bg-indigo-100 transition-colors">
                  <ShieldCheck className="w-4 h-4" />
                  Verify Certificate
                </Link>
              ) : (
                 <Link to="/login" className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors">
                  <Lock className="w-4 h-4" />
                  Login/Signup
                </Link>
              )}
              <button className="md:hidden text-gray-700">
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      {!isAuthPage && (
        <footer className="bg-primary text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
              {/* Company Info */}
              <div className="space-y-4">
                 <div className="flex items-center gap-2 mb-4">
                  <img 
                    src="/logo.png" 
                    alt="Speedlink Logo" 
                    className="w-15 h-10 "
                  />
                  
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Empowering the next generation of tech leaders through world-class training and innovative solutions.
                </p>
                <div className="flex space-x-4 pt-2">
                  <a href="#" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors">
                    <Facebook className="w-4 h-4 text-white" />
                  </a>
                  <a href="#" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors">
                    <Twitter className="w-4 h-4 text-white" />
                  </a>
                  <a href="#" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors">
                    <Linkedin className="w-4 h-4 text-white" />
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-lg font-bold mb-6 border-b border-indigo-800 pb-2 inline-block">Quick Links</h3>
                <ul className="space-y-3 text-sm text-gray-300">
                  <li><a href="#" className="hover:text-white transition-colors flex items-center gap-2">About us</a></li>
                  <li><a href="#" className="hover:text-white transition-colors flex items-center gap-2">Corporate Training</a></li>
                  <li><a href="#" className="hover:text-white transition-colors flex items-center gap-2">Individual Training</a></li>
                  <li><a href="#" className="hover:text-white transition-colors flex items-center gap-2">Certification</a></li>
                  <li><a href="#" className="hover:text-white transition-colors flex items-center gap-2">Blog</a></li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                 <h3 className="text-lg font-bold mb-6 border-b border-indigo-800 pb-2 inline-block">Contact Us</h3>
                 <ul className="space-y-4 text-sm text-gray-300">
                   <li className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>Plot 10, Speedlink Avenue,<br/>Off Ikeja City Mall, Alausa,<br/>Lagos, Nigeria.</span>
                   </li>
                   <li className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                      <span>+234 800 123 4567</span>
                   </li>
                   <li className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                      <span>info@speedlinkng.com</span>
                   </li>
                 </ul>
              </div>

              {/* Map/Extra (Placeholder) */}
              <div className="bg-white/5 rounded-lg p-4 flex items-center justify-center border border-white/10">
                  <div className="text-center">
                    <p className="text-sm font-bold text-white mb-2">Locate Us</p>
                    <p className="text-xs text-gray-300">Find us on Google Maps for easy navigation to our training center.</p>
                  </div>
              </div>
            </div>

            <div className="border-t border-indigo-900 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-400">© 2025 Speedlink Hi-Tech Solutions Limited. All rights reserved.</p>
              <div className="flex gap-6 text-sm text-gray-400">
                <a href="#" className="hover:text-white">Privacy Policy</a>
                <a href="#" className="hover:text-white">Terms of Service</a>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};
